import { z } from 'zod';
import { Task, TaskSchema, CreateTaskSchema } from './base';

// Subtask-related schemas and types
export const SubtaskCreateSchema = CreateTaskSchema.extend({
  parentTaskId: z.string().uuid(),
});

export type SubtaskCreate = z.infer<typeof SubtaskCreateSchema>;

// Task with subtasks schema - define the type first, then the schema
export type TaskWithSubtasks = z.infer<typeof TaskSchema> & {
  subtasks?: TaskWithSubtasks[];
  parentTask?: z.infer<typeof TaskSchema> | null;
};

// Now define the schema with explicit type annotation
export const TaskWithSubtasksSchema: z.ZodSchema<TaskWithSubtasks> = TaskSchema.extend({
  subtasks: z.array(z.lazy(() => TaskWithSubtasksSchema)).optional(),
  parentTask: TaskSchema.nullable().optional(),
}) as z.ZodSchema<TaskWithSubtasks>;

// Subtask utility functions
export class SubtaskUtils {
  /**
   * Calculate the depth of a task based on its parent
   */
  static calculateDepth(parentTask?: { depth?: number } | null): number {
    if (!parentTask) return 0;
    return (parentTask.depth || 0) + 1;
  }

  /**
   * Build the materialized path for a subtask
   */
  static buildPath(taskId: string, parentTask?: { path?: string | null } | null): string {
    if (!parentTask) return taskId;
    const parentPath = parentTask.path || '';
    return parentPath ? `${parentPath}/${taskId}` : taskId;
  }

  /**
   * Check if a task can have subtasks based on settings
   * Default max depth is 1 (only allows parent → child, no grandchildren)
   */
  static canHaveSubtasks(
    task: { depth?: number; settings?: any },
    maxDepth: number = 1
  ): boolean {
    const taskDepth = task.depth || 0;
    const allowSubtasks = task.settings?.allowSubtasks !== false;
    return allowSubtasks && taskDepth < maxDepth;
  }

  /**
   * Get all ancestor IDs from a materialized path
   */
  static getAncestorIds(path?: string | null): string[] {
    if (!path) return [];
    return path.split('/').filter(Boolean);
  }

  /**
   * Check if a task is a descendant of another task
   */
  static isDescendantOf(task: { path?: string | null }, ancestorId: string): boolean {
    if (!task.path) return false;
    return task.path.includes(ancestorId);
  }

  /**
   * Flatten a hierarchical task structure
   */
  static flattenTaskTree(task: TaskWithSubtasks): TaskWithSubtasks[] {
    const result: TaskWithSubtasks[] = [task];
    if (task.subtasks) {
      for (const subtask of task.subtasks) {
        result.push(...this.flattenTaskTree(subtask));
      }
    }
    return result;
  }

  /**
   * Count total subtasks in a tree
   */
  static countSubtasks(task: TaskWithSubtasks): number {
    let count = 0;
    if (task.subtasks) {
      count += task.subtasks.length;
      for (const subtask of task.subtasks) {
        count += this.countSubtasks(subtask);
      }
    }
    return count;
  }

  /**
   * Build a task tree from a flat array
   */
  static buildTaskTree(tasks: Task[]): TaskWithSubtasks[] {
    const taskMap = new Map<string, TaskWithSubtasks>();
    const rootTasks: TaskWithSubtasks[] = [];

    // First pass: create map of all tasks
    for (const task of tasks) {
      taskMap.set(task.id, { ...task, subtasks: [] });
    }

    // Second pass: build tree structure
    for (const task of tasks) {
      const taskWithSubtasks = taskMap.get(task.id)!;
      if (task.parentTaskId) {
        const parent = taskMap.get(task.parentTaskId);
        if (parent) {
          parent.subtasks = parent.subtasks || [];
          parent.subtasks.push(taskWithSubtasks);
        } else {
          // Parent not found, treat as root
          rootTasks.push(taskWithSubtasks);
        }
      } else {
        rootTasks.push(taskWithSubtasks);
      }
    }

    return rootTasks;
  }
} 